import React, { useEffect, useState } from 'react';
import apiService from '../../../services/apiService';

const PRIORITY_COLORS: Record<string, { bg: string; border: string; icon: string }> = {
  info:    { bg: '#EFF6FF', border: '#BFDBFE', icon: 'ℹ️' },
  warning: { bg: '#FFFBEB', border: '#FDE68A', icon: '⚠️' },
  urgent:  { bg: '#FEF2F2', border: '#FECACA', icon: '🚨' },
};

interface AnnouncementBannerProps {
  isAdmin: boolean;
  userId: string;
}

const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({ isAdmin, userId }) => {
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', message: '', priority: 'info' });
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const fetchAnnouncements = async () => {
    try {
      const res = await apiService.getCrmAnnouncements();
      if (res.success) setAnnouncements(res.data);
    } catch (e) {}
  };

  useEffect(() => { fetchAnnouncements(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) return;
    setSaving(true);
    try {
      await apiService.createCrmAnnouncement({ ...form, created_by: userId });
      setForm({ title: '', message: '', priority: 'info' });
      setShowForm(false);
      fetchAnnouncements();
    } catch { alert('Error creating announcement'); }
    finally { setSaving(false); }
  };

  const handleDeactivate = async (id: string) => {
    try {
      await apiService.deactivateAnnouncement(id);
      fetchAnnouncements();
    } catch {}
  };

  const visibleAnnouncements = announcements.filter(a => !dismissed.has(a.id));

  return (
    <div>
      {/* Announcement Banners */}
      {visibleAnnouncements.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {visibleAnnouncements.map(a => {
            const style = PRIORITY_COLORS[a.priority] || PRIORITY_COLORS.info;
            return (
              <div key={a.id} style={{
                background: style.bg, border: `1.5px solid ${style.border}`,
                borderRadius: 'var(--crm-radius)', padding: '12px 16px',
                display: 'flex', alignItems: 'flex-start', gap: 12,
                animation: 'fadeIn 0.3s ease'
              }}>
                <span style={{ fontSize: '18px', flexShrink: 0 }}>{style.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '13.5px', color: 'var(--crm-text-primary)', marginBottom: 2 }}>
                    {a.title}
                  </div>
                  <div style={{ fontSize: '12.5px', color: 'var(--crm-text-secondary)', lineHeight: 1.5 }}>
                    {a.message}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--crm-text-muted)', marginTop: 4 }}>
                    {a.created_by?.name && `— ${a.created_by.name}`} · {new Date(a.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  <button
                    onClick={() => setDismissed(prev => new Set(prev).add(a.id))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: 'var(--crm-text-muted)' }}
                    title="Dismiss"
                  >✕</button>
                  {isAdmin && (
                    <button
                      onClick={() => handleDeactivate(a.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: 'var(--crm-danger)', fontWeight: 600 }}
                      title="Remove for everyone"
                    >🗑</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Admin: Create Announcement Button & Form */}
      {isAdmin && (
        <div style={{ marginBottom: 16 }}>
          {!showForm ? (
            <button 
              className="crm-btn crm-btn-ghost"
              onClick={() => setShowForm(true)}
              style={{ fontSize: '12.5px' }}
            >
              📢 Post Announcement
            </button>
          ) : (
            <form onSubmit={handleCreate} style={{
              background: 'white', border: '1.5px solid var(--crm-border)',
              borderRadius: 'var(--crm-radius)', padding: '16px 20px'
            }}>
              <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: 12 }}>📢 New Announcement for All Staff</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input
                  type="text" placeholder="Announcement Title *" required
                  value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                  style={{ padding: '8px 12px', border: '1.5px solid var(--crm-border)', borderRadius: '6px', fontSize: '13px' }}
                />
                <textarea
                  placeholder="Message to all staff *" required rows={2}
                  value={form.message} onChange={e => setForm({...form, message: e.target.value})}
                  style={{ padding: '8px 12px', border: '1.5px solid var(--crm-border)', borderRadius: '6px', fontSize: '13px', fontFamily: 'Inter, sans-serif', resize: 'vertical' }}
                />
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}
                    style={{ padding: '7px 10px', border: '1.5px solid var(--crm-border)', borderRadius: '6px', fontSize: '13px' }}>
                    <option value="info">ℹ️ Info</option>
                    <option value="warning">⚠️ Warning</option>
                    <option value="urgent">🚨 Urgent</option>
                  </select>
                  <button type="submit" className="crm-btn crm-btn-success" disabled={saving} style={{ fontSize: '12.5px' }}>
                    {saving ? 'Posting…' : '📢 Post to All'}
                  </button>
                  <button type="button" className="crm-btn crm-btn-ghost" onClick={() => setShowForm(false)} style={{ fontSize: '12.5px' }}>
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default AnnouncementBanner;
